// ── Workspace Dashboard ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, closeModal, esc, projectSwatchColor, schemeAccentColor, schemeSurfaceStyle } from '../ui.js';
import { escapeHtml, renderAvatarHtml } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext, refreshProjectList } from '../layout.js';
import { openCreateProjectModal } from '../components/create-project-modal.js';
import { navigate, getQueryParams, setQueryParams, getNavigationId } from '../router.js';
import { loadStatuses, loadWorkspaceStatuses, statusNames, statusLabel, isClosedStatus, closedNames, roleStatus } from '../lib/statuses.js';
import { relativeTime } from '../lib/relative-time.js';
import { can } from '../lib/permissions.js';
import { renderPager, clampOffset } from '../components/pager.js';
import { mountWorkspaceTaskList } from './workspace-tasks.js';
import { expandCard, collapseCard, computeExpandedOrigin, visibleBoundsFor } from '../lib/hover-expand.js';
import { todayIso, dueLabel, selectDueTasks } from '../lib/due-tasks.js';

// ── State ──

let state = {};
function resetState() {
  state = {
    user: null,
    tasks: [], // current My Tasks page (TPT408) — see loadMyTasks()
    myTasks: { offset: 0, limit: 20, total: 0, statusParam: '', loading: false },
    projects: [],
    allTasks: [],
    statuses: [], // cross-project {name,color} union (C1182) — see loadWorkspaceStatuses()
    projectStatuses: {}, // per-project status registries, keyed String(project.id) (C1304) — see loadMetricsAndMembers()
    projectMembers: {},
    metricsLoaded: false,
    projectTaskTotals: {}, // String(project.id) -> { loaded, total } from the per-project task fetch (TPT448)
    membersPromise: null, // member lists across projects, for the banners' member stacks
    allTasksPromise: null,
    allTasksCleanup: null,
    projectHoverCleanup: null,
    navId: null, // router navigation id of the mount (see isLive())
    modalToken: null, // owner token of the latest modal this page opened (TPT647), passed to closeModal()
  };
}
resetState();

// True while `owner` is the mounted dashboard and no navigation happened since it mounted.
// Checked after every await that can resume once the user left: a reply must not land in a
// newer mount's state, nor paint or toast over another page.
function isLive(owner) {
  return state === owner && getNavigationId() === owner.navId;
}

// ── Helpers ──

// Badge/card accent color for a project (C1266) — its color_scheme swatch, or a per-id
// hash color while color_scheme is still 'default'. See projectSwatchColor() in ui.js.
function projectColor(projectId) {
  // Workspace task payloads and /projects can disagree on numeric-vs-string ids. Resolve
  // by value so a task always receives its owning project's scheme instead of the fallback.
  const project = state.projects.find(p => String(p.id) === String(projectId));
  return projectSwatchColor(project && project.color_scheme, projectId);
}

// Owning project's color_scheme, or null (no match yet / still 'default') — same
// id-normalizing lookup projectColor() uses.
function projectScheme(projectId) {
  const project = state.projects.find(p => String(p.id) === String(projectId));
  return project ? project.color_scheme : null;
}

// Inline-style fragment (C1472) carrying --proj-bg/--proj-text/--proj-muted/--proj-track
// for a project-owned surface (card, chip) — '' while color_scheme is still 'default', so
// callers append it directly after --proj-color without an extra branch. See
// schemeSurfaceStyle() in ui.js.
function projectSurface(projectId) {
  return schemeSurfaceStyle(projectScheme(projectId));
}

// Status chip color for one task/segment (C1304): resolves the color TOKEN from that
// project's OWN status registry once loaded (falls back to the cross-project union
// before it lands), then resolves the token to a literal hex inside that SAME project's
// palette via schemeAccentColor() — so a chip always paints the owning project's scheme,
// never the viewer's workspace theme.
function statusColor(projectId, statusName) {
  const ownRows = state.projectStatuses[String(projectId)];
  const row = (ownRows || state.statuses).find(s => s.name === statusName);
  return schemeAccentColor(projectScheme(projectId), row && row.color);
}

// Per-project tally (C1182, TPT400). `counts` is the dynamic per-status-name histogram the
// metrics bar paints — a project's statuses can be arbitrary, so no fixed 4-name shape.
// Canceled-role tasks are excluded from it, completed ones kept so the bar still reflects
// them. `open`/`inProgress`/`mine`/`overdue`/`lastActivity` feed the card's facts row. Roles
// resolve from that project's OWN registry once loaded (C1304 — authoritative about which of
// ITS statuses means canceled/closed/in-progress), the cross-project union before it lands.
// The union carries no is_in_progress flag at all (GET /workspace/statuses only aggregates
// complete/canceled), so the pre-load in-progress fallback is the legacy literal name.
// `overdue` counts open tasks whose due_date (a YYYY-MM-DD string) is before the viewer's
// local day. lastActivity is the newest updated_at across ALL of the project's tasks,
// canceled included.
function computeProjectMetrics(projectId) {
  const rows = state.projectStatuses[String(projectId)] || state.statuses || [];
  const canceledNames = new Set(rows.filter(s => s.is_workflow_canceled).map(s => s.name));
  const closed = new Set(closedNames(rows));
  const inProgressRow = roleStatus(rows, 'in_progress');
  const inProgressName = inProgressRow ? inProgressRow.name : 'in_progress';
  const myId = state.user ? state.user.id : null;
  const today = todayIso();
  const m = { counts: {}, total: 0, open: 0, inProgress: 0, mine: 0, overdue: 0, lastActivity: null };
  for (const t of state.allTasks) {
    if (String(t.project_id) !== String(projectId)) continue;
    if (t.updated_at && (!m.lastActivity || t.updated_at > m.lastActivity)) m.lastActivity = t.updated_at;
    if (canceledNames.has(t.status)) continue;
    m.counts[t.status] = (m.counts[t.status] || 0) + 1;
    m.total++;
    if (closed.has(t.status)) continue;
    m.open++;
    if (t.status === inProgressName) m.inProgress++;
    if (myId != null && t.assignee === myId) m.mine++;
    if (typeof t.due_date === 'string' && t.due_date < today) m.overdue++;
  }
  return m;
}

// The project's tasks from the workspace fetch, id-normalized like projectColor().
function projectTasks(projectId) {
  return state.allTasks.filter(t => String(t.project_id) === String(projectId));
}

function formatGreetingDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

// ── Render: Metrics Bar ──

function renderMetricsBar(metrics, projectId) {
  if (!metrics.total) return '<div class="dash-metrics-bar"></div>';
  const segments = Object.keys(metrics.counts)
    .filter(s => metrics.counts[s] > 0)
    .map(s => {
      const pct = (metrics.counts[s] / metrics.total * 100).toFixed(1);
      return `<div style="width:${pct}%;background:${statusColor(projectId, s)};"></div>`;
    })
    .join('');
  return `<div class="dash-metrics-bar">${segments}</div>`;
}

// ── Render: Member Stack ──

// GET /projects/:id/members also lists pending invites (user_id null, status 'pending');
// the banner counts and paints accepted people only (TPT400).
function acceptedMembers(members) {
  return (members || []).filter(m => m.status === 'accepted');
}

function renderMemberStack(members) {
  const accepted = acceptedMembers(members);
  if (!accepted.length) return '';
  const show = accepted.slice(0, 5);
  const extra = accepted.length - show.length;
  let html = show.map(m => renderAvatarHtml(m, 28)).join('');
  if (extra > 0) {
    html += `<div class="dash-member-more">+${extra}</div>`;
  }
  return html;
}

// ── Render: My Task Row ──

function renderMyTaskRow(task) {
  const color = projectColor(task.project_id);
  // Own project's status names once loaded — a project with custom names otherwise gets
  // offered the union's names (C1304); union stays the pre-load fallback.
  const ownRows = state.projectStatuses[String(task.project_id)];
  const options = statusNames(ownRows || state.statuses).map(s =>
    `<option value="${escapeHtml(s)}"${s === task.status ? ' selected' : ''}>${escapeHtml(statusLabel(s))}</option>`
  ).join('');
  // A project the caller can only view shows the status as a plain pill. Decided from the
  // role on the GET /projects row; an unknown project keeps the select (the API decides).
  const project = state.projects.find(p => String(p.id) === String(task.project_id));
  const readOnly = !!project && !can(project, getUser(), 'task:write');
  const statusControl = readOnly
    ? `<span class="badge badge-status dash-status-static" style="--status-color:${statusColor(task.project_id, task.status)}">${escapeHtml(statusLabel(task.status))}</span>`
    : `<select class="form-select task-status-select dash-status-select" data-task-key="${escapeHtml(task.task_key)}" data-project-id="${task.project_id}" style="--status-color:${statusColor(task.project_id, task.status)}">${options}</select>`;
  return `<div class="dash-task-row" data-task-key="${escapeHtml(task.task_key)}" data-project-id="${task.project_id}">
    <span class="ws-project-badge" style="--proj-color:${color}${projectSurface(task.project_id)}">${escapeHtml(task.project_name || '')}</span>
    <a class="dash-task-key task-key-link" href="#/projects/${task.project_id}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>
    <span class="dash-task-title">${escapeHtml(task.title)}</span>
    ${statusControl}
  </div>`;
}

// ── Render: My Tasks Section ──

function renderMyTasks() {
  renderMyTasksPager();
  const el = document.getElementById('dash-my-tasks');
  if (!el) return;
  if (!state.tasks.length) {
    el.innerHTML = '<div class="dash-empty">No active tasks assigned to you</div>';
    return;
  }
  el.innerHTML = state.tasks.map(renderMyTaskRow).join('');
}

// Gmail-style "1–20 of 55" + prev/next chevrons in the section head, right of the title
// (TPT408). Hidden while the list is empty; both arrows greyed while a page is loading.
function renderMyTasksPager() {
  const el = document.getElementById('dash-my-tasks-pager');
  if (!el) return;
  const mt = state.myTasks;
  el.innerHTML = renderPager({ offset: mt.offset, limit: mt.limit, total: mt.total, scope: 'my', disabled: mt.loading });
}

// ── Render: Project Card (banner, TPT400) ──

// One facts cell: value on top, small uppercase label below. `—` before enrichment lands
// so the row keeps its height and never shifts the grid.
function renderFact(value, label, cls, title) {
  return `<div class="dash-fact${cls ? ' ' + cls : ''}"${title ? ` title="${escapeHtml(title)}"` : ''}>`
    + `<span class="dash-fact-value">${escapeHtml(String(value))}</span>`
    + `<span class="dash-fact-label">${escapeHtml(label)}</span></div>`;
}

function renderProjectFacts(project) {
  const m = state.metricsLoaded ? computeProjectMetrics(project.id) : null;
  const totals = state.projectTaskTotals[String(project.id)];
  const partial = totals && totals.loaded < totals.total
    ? `Counts cover the first ${totals.loaded} of ${totals.total} tasks in this project`
    : '';
  return renderFact(m ? m.open : '\u2014', 'open', '', partial)
    + renderFact(m ? m.inProgress : '\u2014', 'in progress', 'dash-fact--in-progress', partial)
    + renderFact(m ? m.mine : '\u2014', 'mine', '', partial)
    + renderFact(m ? m.overdue : '\u2014', 'overdue', m && m.overdue ? 'dash-fact--overdue' : '', partial);
}

// "5 members" caption next to the avatar stack; '' until the member fetch lands.
function renderMemberCount(project) {
  const members = state.projectMembers[project.id];
  if (!members) return '';
  const n = acceptedMembers(members).length;
  return `${n} ${n === 1 ? 'member' : 'members'}`;
}

// "Updated 4 hrs ago" from the newest updated_at in state.allTasks for this project;
// '' before enrichment, "No activity yet" for a project with no tasks.
function renderProjectActivity(project) {
  if (!state.metricsLoaded) return '';
  const rel = relativeTime(computeProjectMetrics(project.id).lastActivity);
  return rel ? `Updated ${escapeHtml(rel)}` : 'No activity yet';
}

const DUE_TASK_COUNT = 3;
const RECENT_TASK_COUNT = 3;

// Status legend under the bar: one dot + label + count per status that has tasks, in the
// project's own status order. '' before enrichment (the container reserves the line).
function renderProjectLegend(project) {
  if (!state.metricsLoaded) return '';
  const m = computeProjectMetrics(project.id);
  const rows = state.projectStatuses[String(project.id)] || state.statuses || [];
  const ordered = statusNames(rows).filter(n => m.counts[n] > 0);
  Object.keys(m.counts).forEach(n => { if (m.counts[n] > 0 && !ordered.includes(n)) ordered.push(n); });
  if (!ordered.length) return '<span class="dash-details-muted">No tasks yet</span>';
  return ordered.map(n =>
    `<span class="dash-status-chip"><span class="dash-status-dot" style="background:${statusColor(project.id, n)}"></span>${escapeHtml(statusLabel(n))}<span class="dash-status-count">${m.counts[n]}</span></span>`
  ).join('');
}

// One task line of the details body: key permalink, title, right-aligned caption.
function renderDetailTaskRow(project, task, caption, captionCls) {
  return `<div class="dash-recent-task">`
    + `<a class="task-key-link dash-recent-key" href="#/projects/${project.id}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>`
    + `<span class="dash-recent-title">${escapeHtml(task.title || '')}</span>`
    + `<span class="dash-recent-time${captionCls ? ' ' + captionCls : ''}">${escapeHtml(caption || '')}</span></div>`;
}

function renderDetailSection(label, bodyHtml) {
  return `<div class="dash-details-section"><div class="dash-details-label">${escapeHtml(label)}</div>${bodyHtml}</div>`;
}

// Hidden hover-expand body (TPT401, TPT434): everything the compact banner leaves out —
// description, facts row, member stack, the open tasks due soonest, and the newest tasks.
// Always rendered so the block is in the DOM, collapsed by CSS until the card gets
// `.is-expanded`.
function renderProjectDetails(project) {
  const parts = [];
  if (project.description) {
    parts.push(`<div class="dash-project-details-desc">${escapeHtml(project.description)}</div>`);
  }
  parts.push(`<div class="dash-project-facts" id="dash-facts-${project.id}">${renderProjectFacts(project)}</div>`);
  parts.push(`<div class="dash-project-footer">`
    + `<div class="dash-member-stack" id="dash-members-${project.id}">${renderMemberStack(state.projectMembers[project.id])}</div>`
    + `<span class="dash-member-count">${renderMemberCount(project)}</span></div>`);
  if (!state.metricsLoaded) {
    parts.push('<div class="dash-details-muted">Loading…</div>');
  } else {
    const tasks = projectTasks(project.id);
    if (tasks.length) {
      const rows = state.projectStatuses[String(project.id)] || state.statuses || [];
      const due = selectDueTasks(tasks, { closed: closedNames(rows), limit: DUE_TASK_COUNT });
      const more = due.total - due.rows.length;
      parts.push(renderDetailSection('Due', due.rows.length
        ? `<div class="dash-recent-tasks">${due.rows.map(r =>
          renderDetailTaskRow(project, r.task, dueLabel(r.diff, r.task.due_date), 'dash-due-when' + (r.overdue ? ' is-overdue' : ''))
        ).join('')}</div>${more > 0 ? `<div class="dash-details-muted">+${more} more</div>` : ''}`
        : '<div class="dash-details-muted">No due dates on open tasks</div>'));
    }
    const recent = tasks
      .filter(t => t.updated_at)
      .sort((a, b) => (a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : 0))
      .slice(0, RECENT_TASK_COUNT);
    if (recent.length) {
      parts.push(renderDetailSection('Recently updated',
        `<div class="dash-recent-tasks">${recent.map(t =>
          renderDetailTaskRow(project, t, relativeTime(t.updated_at))
        ).join('')}</div>`));
    }
  }
  return `<div class="dash-project-details-inner">${parts.join('')}</div>`;
}

// Compact banner (TPT434): title row, status bar and its legend stay visible; the rest lives
// in the hover-expand body. --dash-danger is "red" in the project's own palette (overdue).
function renderProjectCard(project) {
  const metricsHtml = state.metricsLoaded
    ? renderMetricsBar(computeProjectMetrics(project.id), project.id)
    : '<div class="dash-metrics-bar"></div>';

  const color = projectColor(project.id);
  const danger = schemeAccentColor(projectScheme(project.id), 'red');
  return `<div class="card card-clickable dash-project-card" data-id="${project.id}" tabindex="0" aria-label="${escapeHtml(project.name)} project" style="--dash-danger:${danger};--proj-color:${color}${projectSurface(project.id)}">
    <div class="dash-project-title-row">
      <span class="dash-project-swatch"></span>
      <div class="card-title">${escapeHtml(project.name)}</div>
      <span class="dash-project-activity" id="dash-activity-${project.id}">${renderProjectActivity(project)}</span>
    </div>
    <div class="dash-metrics-wrap" id="dash-metrics-${project.id}">${metricsHtml}</div>
    <div class="dash-status-legend dash-project-legend" id="dash-legend-${project.id}">${renderProjectLegend(project)}</div>
    <div class="dash-project-details" id="dash-details-${project.id}">${renderProjectDetails(project)}</div>
  </div>`;
}

// ── Render: Projects Grid ──

function renderProjects() {
  const el = document.getElementById('dash-project-grid');
  if (!el) return;
  if (!state.projects.length) {
    el.innerHTML = `<div class="card card-clickable dash-new-project" id="dash-new-project">
      <span class="dash-new-project-icon">+</span><span>New Project</span>
    </div>`;
    return;
  }
  el.innerHTML = state.projects.map(renderProjectCard).join('')
    + `<div class="card card-clickable dash-new-project" id="dash-new-project">
        <span class="dash-new-project-icon">+</span><span>New Project</span>
      </div>`;
}

function getDashboardTab() {
  return getQueryParams().get('tab') === 'all' ? 'all' : 'my';
}

// The top row sits above both panels: greeting on the left, the My Tasks / All Tasks tab
// strip on the right (TPT433). The My Tasks panel holds the Projects grid (banners) first,
// then the task rows (TPT400). All Tasks is mounted lazily from workspace-tasks.js.
export function renderDashboard() {
  const activeTab = getDashboardTab();
  return `
    <div class="dash-top-row">
      <div class="dash-header">
        <div class="dash-welcome">
          ${renderAvatarHtml(state.user, 48)}
          <div>
            <h2>Welcome, ${esc(state.user.name || 'there')}</h2>
            <p class="text-muted">${formatGreetingDate()}</p>
          </div>
        </div>
      </div>
      <div class="dash-tabs" role="tablist" aria-label="Dashboard views">
        <button class="dash-tab${activeTab === 'my' ? ' active' : ''}" id="dash-tab-my" type="button" role="tab" aria-controls="dash-panel-my" aria-selected="${activeTab === 'my'}" tabindex="${activeTab === 'my' ? '0' : '-1'}" data-tab="my">My Tasks</button>
        <button class="dash-tab${activeTab === 'all' ? ' active' : ''}" id="dash-tab-all" type="button" role="tab" aria-controls="dash-panel-all" aria-selected="${activeTab === 'all'}" tabindex="${activeTab === 'all' ? '0' : '-1'}" data-tab="all">All Tasks</button>
      </div>
    </div>
    <section class="dash-tab-panel" id="dash-panel-my" role="tabpanel" aria-labelledby="dash-tab-my"${activeTab === 'my' ? '' : ' hidden'}>
      <section class="dash-section dash-projects-section">
        <h3 class="dash-section-title">Projects</h3>
        <div class="grid dash-project-grid" id="dash-project-grid">
          <div class="empty"><span class="spinner"></span></div>
        </div>
      </section>
      <section class="dash-section">
        <div class="dash-section-head">
          <h3 class="dash-section-title">My Tasks</h3>
          <div class="dash-my-tasks-pager" id="dash-my-tasks-pager"></div>
        </div>
        <div class="dash-my-tasks" id="dash-my-tasks">
          <div class="empty"><span class="spinner"></span></div>
        </div>
      </section>
    </section>
    <section class="dash-tab-panel" id="dash-panel-all" role="tabpanel" aria-labelledby="dash-tab-all"${activeTab === 'all' ? '' : ' hidden'}>
      <div id="dash-all-tasks"><div class="empty"><span class="spinner"></span></div></div>
    </section>`;
}

function updateDashboardTabQuery(tab) {
  const params = getQueryParams();
  if (tab === 'all') params.set('tab', 'all');
  else params.delete('tab');
  setQueryParams(params);
}

// Switch dashboard panels and mount All Tasks once. Keeping the promise in state makes
// repeated clicks and keyboard activation share the same request and list instance.
export async function activateDashboardTab(tab, { updateUrl = true } = {}) {
  const activeTab = tab === 'all' ? 'all' : 'my';
  const content = getContentEl();
  if (!content) return;

  content.querySelectorAll('.dash-tab').forEach(button => {
    const isActive = button.dataset.tab === activeTab;
    button.classList.toggle('active', isActive);
    button.setAttribute('aria-selected', String(isActive));
    button.tabIndex = isActive ? 0 : -1;
  });
  content.querySelectorAll('.dash-tab-panel').forEach(panel => {
    panel.hidden = panel.id === 'dash-panel-' + activeTab ? false : true;
  });

  if (updateUrl) updateDashboardTabQuery(activeTab);

  if (activeTab !== 'all' || state.allTasksPromise) return state.allTasksPromise;

  const mount = content.querySelector('#dash-all-tasks');
  if (!mount) return;

  const owner = state;
  const promise = mountWorkspaceTaskList(mount, {
    includeHeader: true,
    projects: state.projects,
  }).catch(err => {
    if (isLive(owner)) showToast(err.message || 'Failed to load all tasks', 'error');
    return null;
  });
  state.allTasksPromise = promise;
  // A list that resolves after the dashboard is gone is released at once: the dashboard's
  // teardown already ran without it, so storing it would leak its filter-bar binding.
  promise.then(cleanup => {
    if (isLive(owner)) owner.allTasksCleanup = cleanup;
    else if (typeof cleanup === 'function') cleanup();
  });
  return promise;
}

// ── Background: Metrics + Members ──

// GET /workspace/tasks caps `limit` at 200 and returns `total`. Tasks are fetched PER PROJECT
// (`project_id` filter, TPT448) so a large project can never crowd a small one out of a
// shared, priority-ordered page budget. Each project pages through the rest in parallel,
// capped at PROJECT_TASK_PAGES pages (1000 tasks) — beyond that that project's facts row
// says so via a tooltip.
const PROJECT_TASK_PAGE = 200;
const PROJECT_TASK_PAGES = 5;

async function loadProjectTasks(projectId) {
  const base = `/workspace/tasks?project_id=${encodeURIComponent(projectId)}&limit=${PROJECT_TASK_PAGE}`;
  const first = await api.get(base);
  let tasks = first.tasks || [];
  const total = Number.isFinite(Number(first.total)) ? Number(first.total) : tasks.length;
  const pages = Math.min(Math.ceil(total / PROJECT_TASK_PAGE), PROJECT_TASK_PAGES);
  if (pages > 1) {
    const rest = await Promise.allSettled(Array.from({ length: pages - 1 }, (_, i) =>
      api.get(`${base}&offset=${(i + 1) * PROJECT_TASK_PAGE}`)));
    for (const r of rest) {
      if (r.status === 'fulfilled') tasks = tasks.concat(r.value.tasks || []);
    }
  }
  // Pages are ordered by priority/created_at, not a stable cursor — a task created
  // mid-fetch can shift one onto two pages, so dedupe by id.
  const seen = new Set();
  tasks = tasks.filter(t => !seen.has(t.id) && seen.add(t.id));
  return { tasks, total: Math.max(total, tasks.length) };
}

// One GET /projects/:id/members per project, memoised, for the banners' member stacks. A
// failed project just contributes no members. (The All Tasks filter bar gets its own
// narrowed member list from GET /workspace/filter-options, TPT453.)
function loadProjectMembers() {
  const owner = state;
  if (!state.membersPromise) {
    state.membersPromise = Promise.all(state.projects.map(p =>
      api.get('/projects/' + p.id + '/members')
        .then(res => {
          if (!isLive(owner)) return [];
          state.projectMembers[p.id] = res.members || [];
          updateMemberStack(p.id);
          return res.members || [];
        })
        .catch(() => [])
    )).then(lists => [].concat(...lists));
  }
  return state.membersPromise;
}

async function loadMetricsAndMembers() {
  const owner = state;
  // Fetch every project's tasks for metrics — unfiltered by status (C1182):
  // computeProjectMetrics() tallies whatever status names actually occur, not a fixed
  // 4-name shape, so a server-side status= filter would silently under-count any project's
  // custom names. One failed project leaves only its own card empty.
  try {
    const results = await Promise.allSettled(state.projects.map(p => loadProjectTasks(p.id)));
    if (!isLive(owner)) return;
    const all = [];
    results.forEach((r, i) => {
      if (r.status !== 'fulfilled') return;
      all.push(...r.value.tasks);
      state.projectTaskTotals[String(state.projects[i].id)] = { loaded: r.value.tasks.length, total: r.value.total };
    });
    state.allTasks = all;
    state.metricsLoaded = true;
    updateAllProjectFacts();
  } catch (_) { /* skip */ }

  // Fetch members + each project's own status registry in parallel (C1304). loadStatuses()
  // is memoised per project id and never rejects (falls back to the legacy 5) — same call
  // updateTaskStatus() already makes, so this shares its cache.
  const memberFetches = [loadProjectMembers()];
  const statusFetches = state.projects.map(p =>
    loadStatuses(p.id).then(rows => { if (isLive(owner)) state.projectStatuses[String(p.id)] = rows; })
  );
  await Promise.allSettled([...memberFetches, ...statusFetches]);
  if (!isLive(owner)) return;

  // Statuses land after the union-based first paint above — repaint metrics segments,
  // facts, and My Tasks rows/options with each project's own palette/roles now that they're
  // known (C1304).
  updateAllProjectFacts();
  renderMyTasks();
}

// Repaint one card's hover-expand body (description, facts row, member stack, due + recent
// tasks) in place.
function updateProjectDetails(project) {
  const details = document.getElementById('dash-details-' + project.id);
  if (!details) return;
  details.innerHTML = renderProjectDetails(project);
  // Enrichment can land while the card is hovered open — re-fit the overlay to the new body.
  const card = details.closest('.dash-project-card');
  if (card && card.classList.contains('is-expanded')) fitExpandedProjectCard(card);
}

// Repaint one card's enrichment-driven parts in place: metrics bar, legend, activity, and the
// details body (which holds the facts row).
function updateProjectFacts(project) {
  const wrap = document.getElementById('dash-metrics-' + project.id);
  if (wrap) wrap.innerHTML = renderMetricsBar(computeProjectMetrics(project.id), project.id);
  const legend = document.getElementById('dash-legend-' + project.id);
  if (legend) legend.innerHTML = renderProjectLegend(project);
  const activity = document.getElementById('dash-activity-' + project.id);
  if (activity) activity.innerHTML = renderProjectActivity(project);
  updateProjectDetails(project);
}

function updateAllProjectFacts() {
  for (const p of state.projects) updateProjectFacts(p);
}

// The member stack and its count live in the details body.
function updateMemberStack(projectId) {
  const project = state.projects.find(p => String(p.id) === String(projectId));
  if (project) updateProjectDetails(project);
}

// ── Hover expand (TPT401, TPT434) ──
//
// Reuses lib/hover-expand.js: expandCard() turns the card into a position:fixed overlay and
// leaves a `.task-card-placeholder` in its grid slot, so sibling cards never reflow. This
// layer keeps the overlay exactly over that slot (`.is-floating`), adds `.is-expanded` (CSS
// reveals `.dash-project-details`) and sizes/positions the overlay for the extra height.
//
// The card is only ever in the grid at its collapsed size: it floats before the details open
// and goes back after they have closed, so the row height never changes on hover in or out.

const PROJECT_EXPAND_DELAY = 150; // ms hover dwell — same as the boards
const PROJECT_COLLAPSE_DELAY = 100;
const PROJECT_EDGE_MARGIN = 10;
// The details' max-height transition in styles.css (.dash-project-details) plus slack — the
// fallback for a collapse whose transitionend never fires (nothing to animate, tab hidden).
const PROJECT_COLLAPSE_FALLBACK = 200;

function prefersReducedMotion() {
  return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Size the floating card to its slot + details height and re-clamp its position so a card
// near the viewport bottom grows upward instead of off-screen. Safe to re-run.
function fitExpandedProjectCard(card) {
  const details = card.querySelector('.dash-project-details');
  const placeholder = card._hoverExpandPlaceholder;
  if (!details || !placeholder) return;
  // Same width as the slot, so the collapsed part wraps exactly as it does in the grid.
  card.style.width = placeholder.getBoundingClientRect().width + 'px';
  card.style.height = '';
  card.style.minHeight = '';
  card.style.maxHeight = '';
  const detailsH = details.scrollHeight;
  // Collapsed height the card's own content needs (the CSS min-height included).
  const natural = Math.max(
    card.getBoundingClientRect().height - details.getBoundingClientRect().height,
    parseFloat(window.getComputedStyle(card).minHeight) || 0,
  );
  // The placeholder stands in for the in-flow card: its own content height as a floor, then
  // stretched by the grid to the row — so the slot follows the row even if it changes while
  // the card floats (enrichment landing, window resize).
  placeholder.style.height = 'auto';
  placeholder.style.minHeight = natural + 'px';
  const slot = placeholder.getBoundingClientRect();
  // A card the grid stretched to a taller neighbour keeps that height while it floats.
  card.style.minHeight = slot.height + 'px';
  card.style.setProperty('--dash-details-h', detailsH + 'px');
  const bounds = visibleBoundsFor();
  const cap = Math.max(slot.height, bounds.bottom - bounds.top - 2 * PROJECT_EDGE_MARGIN);
  const target = Math.min(Math.max(slot.height, natural + detailsH), cap);
  const { left, top } = computeExpandedOrigin(slot, bounds, slot.width, target, { topGrow: 0 });
  card.style.left = left + 'px';
  card.style.top = top + 'px';
  card.style.maxHeight = cap + 'px';
  card.style.overflowY = natural + detailsH > cap ? 'auto' : '';
}

function cancelPendingProjectCollapse(card) {
  clearTimeout(card._dashCollapseTimer);
  card._dashCollapseTimer = null;
  if (card._dashCollapseEnd) {
    card.removeEventListener('transitionend', card._dashCollapseEnd);
    card._dashCollapseEnd = null;
  }
}

function expandProjectCard(grid, card) {
  if (card.classList.contains('is-expanded')) return;
  if (card._hoverExpandPlaceholder) {
    // Still floating from a collapse that hasn't finished — reopen it in place.
    cancelPendingProjectCollapse(card);
    fitExpandedProjectCard(card);
    card.classList.add('is-expanded');
    return;
  }
  const rect = card.getBoundingClientRect();
  expandCard(card, grid);
  if (!card._hoverExpandPlaceholder) return; // expandCard() declined (drag in progress)
  // expandCard() widens a board card and nudges it upward; a banner starts exactly over its
  // slot instead, so nothing moves until the details open. expandCard() has already laid the
  // card out at its nudged position, so the move back must not run `.is-floating`'s `top`
  // transition: it is off until the fit below has measured the card in place. Restoring it
  // afterwards lets only a bottom-clamp shift animate.
  card.style.transition = 'none';
  card.style.left = rect.left + 'px';
  card.style.top = rect.top + 'px';
  card.style.width = rect.width + 'px';
  card.style.height = '';
  card.classList.add('is-floating');
  fitExpandedProjectCard(card);
  card.style.transition = '';
  card.classList.add('is-expanded');
}

// Put a floating card back into its grid slot.
function finishProjectCollapse(card) {
  cancelPendingProjectCollapse(card);
  card.classList.remove('is-expanded', 'is-floating');
  card.style.minHeight = '';
  card.style.maxHeight = '';
  card.style.removeProperty('--dash-details-h');
  collapseCard(card);
}

// Close the details first and only then return the card to the grid: it stays a fixed overlay
// (placeholder holding the slot) until the details' max-height transition ends, so the row
// never sees a card taller than its collapsed size. `immediate` skips the animation — page
// scroll, unbind, reduced motion.
function collapseProjectCard(card, { immediate = false } = {}) {
  const placeholder = card._hoverExpandPlaceholder;
  if (!placeholder) return; // not floating
  if (immediate || prefersReducedMotion()) { finishProjectCollapse(card); return; }
  if (!card.classList.contains('is-expanded')) return; // already closing
  card.classList.remove('is-expanded');
  // Slide back over the slot while the details close (a bottom-clamped card sits above it).
  card.style.top = placeholder.getBoundingClientRect().top + 'px';
  const details = card.querySelector('.dash-project-details');
  const onEnd = (e) => {
    if (e.target === details && e.propertyName === 'max-height') finishProjectCollapse(card);
  };
  card._dashCollapseEnd = onEnd;
  card.addEventListener('transitionend', onEnd);
  card._dashCollapseTimer = setTimeout(() => finishProjectCollapse(card), PROJECT_COLLAPSE_FALLBACK);
}

// Delegated on the grid so it survives renderProjects() re-renders. Returns an unbind fn.
function bindProjectCardHover(grid) {
  if (!grid) return () => {};
  const selector = '.dash-project-card[data-id]';
  const expandTimers = new WeakMap();
  const collapseTimers = new WeakMap();
  const cardOf = (e) => {
    const card = e.target.closest ? e.target.closest(selector) : null;
    return card && grid.contains(card) ? card : null;
  };
  const canHover = () => !window.matchMedia || window.matchMedia('(hover: hover)').matches;

  const onEnter = (e) => {
    const card = cardOf(e);
    if (!card || !canHover()) return; // touch: a tap just navigates
    clearTimeout(collapseTimers.get(card));
    clearTimeout(expandTimers.get(card));
    expandTimers.set(card, setTimeout(() => {
      if (document.body.contains(card)) expandProjectCard(grid, card);
    }, PROJECT_EXPAND_DELAY));
  };
  const onLeave = (e) => {
    const card = cardOf(e);
    if (!card || card.contains(e.relatedTarget)) return;
    clearTimeout(expandTimers.get(card));
    clearTimeout(collapseTimers.get(card));
    collapseTimers.set(card, setTimeout(() => {
      if (document.body.contains(card)) collapseProjectCard(card);
    }, PROJECT_COLLAPSE_DELAY));
  };
  const onFocusIn = (e) => {
    const card = cardOf(e);
    if (!card) return;
    clearTimeout(collapseTimers.get(card));
    expandProjectCard(grid, card);
  };
  const onFocusOut = (e) => {
    const card = cardOf(e);
    if (!card || card.contains(e.relatedTarget)) return;
    clearTimeout(expandTimers.get(card));
    collapseProjectCard(card);
  };
  const onKeyDown = (e) => {
    if ((e.key !== 'Enter' && e.key !== ' ') || e.target.closest('a')) return;
    const card = cardOf(e);
    if (!card || e.target !== card) return;
    e.preventDefault();
    navigate('/projects/' + card.dataset.id + '/board');
  };
  // A fixed overlay doesn't follow page scroll — drop it at once, closing or not (the overlay's
  // own scroll is fine).
  const dropFloating = () => {
    grid.querySelectorAll('.dash-project-card.is-floating').forEach(card => collapseProjectCard(card, { immediate: true }));
  };
  const onScroll = (e) => {
    const t = e.target;
    if (t && t.nodeType === 1 && t.closest('.dash-project-card.is-floating')) return;
    dropFloating();
  };

  grid.addEventListener('mouseenter', onEnter, true);
  grid.addEventListener('mouseleave', onLeave, true);
  grid.addEventListener('focusin', onFocusIn);
  grid.addEventListener('focusout', onFocusOut);
  grid.addEventListener('keydown', onKeyDown);
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  return () => {
    dropFloating();
    grid.removeEventListener('mouseenter', onEnter, true);
    grid.removeEventListener('mouseleave', onLeave, true);
    grid.removeEventListener('focusin', onFocusIn);
    grid.removeEventListener('focusout', onFocusOut);
    grid.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('scroll', onScroll, { capture: true });
  };
}

// ── My Tasks: paged fetch (TPT408) ──

function fetchMyTasksPage(offset) {
  const mt = state.myTasks;
  return api.get(`/workspace/tasks?assignee=me${mt.statusParam}&limit=${mt.limit}&offset=${offset}`);
}

// Loads one page of the caller's open tasks into state.tasks. The server's `total` honours
// the same assignee/status filter, so the pager's range is exact. A stale offset (the last
// row of the last page just closed, or the count shrank) is clamped back onto a real page
// and refetched once. `render:false` lets the first paint wait for the projects list so
// rows get their project colours on the first render.
async function loadMyTasks({ offset = state.myTasks.offset, render = true } = {}) {
  const owner = state;
  const mt = state.myTasks;
  mt.offset = Math.max(0, offset);
  mt.loading = true;
  if (render) renderMyTasksPager();
  try {
    let res = await fetchMyTasksPage(mt.offset);
    if (!isLive(owner)) return;
    const total = Number(res.total) || 0;
    const clamped = clampOffset(mt.offset, mt.limit, total);
    if (clamped !== mt.offset) {
      mt.offset = clamped;
      res = await fetchMyTasksPage(clamped);
      if (!isLive(owner)) return;
    }
    state.tasks = res.tasks || [];
    mt.total = Number(res.total) || state.tasks.length;
  } catch (err) {
    if (!isLive(owner)) return;
    showToast(err.message || 'Failed to load your tasks', 'error');
  }
  mt.loading = false;
  if (render) renderMyTasks();
}

// ── Status Update ──

async function updateTaskStatus(projectId, taskKey, newStatus) {
  const owner = state;
  try {
    await api.patch('/projects/' + projectId + '/tasks/' + taskKey, { status: newStatus });
    // This task's own project's role registry (not the cross-project union) resolves
    // whether newStatus actually means "done" — complete OR canceled role (C1187).
    const projectStatuses = await loadStatuses(projectId);
    if (!isLive(owner)) return;
    const isDone = isClosedStatus(projectStatuses, newStatus);
    if (isDone) {
      // Refetch the current page instead of splicing the row out client-side — keeps the
      // pager's total truthful and pulls the next task up onto this page (TPT408).
      await loadMyTasks({ offset: state.myTasks.offset });
    } else {
      const task = state.tasks.find(t => t.task_key === taskKey);
      if (task) task.status = newStatus;
    }
    showToast('Status updated');
  } catch (err) {
    if (!isLive(owner)) return;
    showToast(err.message || 'Failed to update', 'error');
    renderMyTasks();
  }
}

// ── Events ──

function bindDashboardTabs() {
  const content = getContentEl();
  if (!content) return;

  const tabs = [...content.querySelectorAll('.dash-tab')];
  tabs.forEach((button, index) => {
    button.addEventListener('click', () => {
      void activateDashboardTab(button.dataset.tab);
    });
    button.addEventListener('keydown', (e) => {
      let nextIndex = index;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (e.key === 'Home') nextIndex = 0;
      if (e.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === index) return;
      e.preventDefault();
      tabs[nextIndex].focus();
      void activateDashboardTab(tabs[nextIndex].dataset.tab);
    });
  });
}

function bindEvents() {
  const content = getContentEl();
  if (!content) return;

  content.addEventListener('click', (e) => {
    // My Tasks pager arrows (TPT408) — scoped so All Tasks' own pager is not ours
    const pagerBtn = e.target.closest('button[data-pager][data-pager-scope="my"]');
    if (pagerBtn) {
      if (pagerBtn.disabled || state.myTasks.loading) return;
      const delta = pagerBtn.dataset.pager === 'prev' ? -1 : 1;
      void loadMyTasks({ offset: state.myTasks.offset + delta * state.myTasks.limit });
      return;
    }
    // New project card
    if (e.target.closest('#dash-new-project')) {
      state.modalToken = openCreateProjectModal();
      return;
    }
    // Recent-task permalinks inside a project card follow their own href (TPT401)
    if (e.target.closest('.dash-project-card a')) return;
    // Project card
    const card = e.target.closest('.dash-project-card[data-id]');
    if (card) {
      navigate('/projects/' + card.dataset.id + '/board');
      return;
    }
    // Task row (skip if clicking select)
    const row = e.target.closest('.dash-task-row');
    if (row && !e.target.closest('select, a')) {
      navigate('/projects/' + row.dataset.projectId + '/board');
      return;
    }
  });

  content.addEventListener('change', (e) => {
    const sel = e.target.closest('.dash-status-select');
    if (sel) {
      updateTaskStatus(Number(sel.dataset.projectId), sel.dataset.taskKey, sel.value);
    }
  });
}

// ── Page Entry ──

export async function dashboardPage() {
  // Read before the first await: once it changes, the user navigated on and this mount stops
  // after its next await (see router.js getNavigationId()).
  const navId = getNavigationId();
  if (!(await checkAuth()) || getNavigationId() !== navId) return;
  resetState();
  const owner = state;
  state.navId = navId;
  state.user = getUser();

  initLayout(state.user);
  updateLayoutContext({ projectId: null, projectName: null, activeView: 'dashboard', viewLabel: 'Dashboard' });

  const content = getContentEl();
  content.innerHTML = renderDashboard();

  // Parallel fetch: cross-project status union + projects list; "my tasks" waits on the
  // status union (C1182) so its own status= filter can name whatever's actually active
  // across every project instead of the old fixed 3-name literal list.
  const statusesPromise = loadWorkspaceStatuses();
  const projectsPromise = api.get('/projects');
  // Awaited below; this keeps its rejection handled when the mount stops before reaching it.
  projectsPromise.catch(() => {});
  try {
    // Not `state.statuses = await …`: that binds the state object before the await.
    const statuses = await statusesPromise;
    if (!isLive(owner)) return;
    state.statuses = statuses;
    const closed = new Set(closedNames(state.statuses));
    const activeNames = statusNames(state.statuses).filter(n => !closed.has(n));
    state.myTasks.statusParam = activeNames.length ? `&status=${activeNames.map(encodeURIComponent).join(',')}` : '';
    const [, projectsRes] = await Promise.all([
      loadMyTasks({ offset: 0, render: false }),
      projectsPromise,
    ]);
    if (!isLive(owner)) return;
    state.projects = projectsRes.projects || [];
  } catch (err) {
    if (!isLive(owner)) return;
    showToast(err.message || 'Failed to load dashboard', 'error');
  }

  renderMyTasks();
  renderProjects();
  state.projectHoverCleanup = bindProjectCardHover(document.getElementById('dash-project-grid'));
  bindEvents();
  bindDashboardTabs();
  void activateDashboardTab(getDashboardTab(), { updateUrl: false });

  // Background enrichment
  loadMetricsAndMembers();
  refreshProjectList();

  return () => {
    // A late teardown (router.js runs it as soon as it resolves) must leave a newer mount of
    // this page alone: that mount owns the module state by then.
    if (state !== owner) return;
    if (state.allTasksCleanup) state.allTasksCleanup();
    if (state.projectHoverCleanup) state.projectHoverCleanup();
    closeModal(state.modalToken);
    resetState();
  };
}
